// @ts-nocheck
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import React, { createContext, FC, PropsWithChildren, useContext } from "react";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";

/**
 * Context model for modal overlay behavior.
 *
 * @property scrollElement - The element whose scroll position/state should be used
 * by modal consumers. `null` indicates no scroll container is available.
 */
interface ModalOverlayContextModel {
    scrollElement: HTMLElement | null;
}
const mobileBreakpoint: BreakpointKey = "xs";

const ModalOverlayContext = createContext<ModalOverlayContextModel | null>(null);

interface ModalOverlayProviderProps {
    scrollElement: HTMLElement | null;
}

/**
 * Provides modal overlay context to descendants.
 */
export const ModalOverlayProvider: FC<PropsWithChildren<ModalOverlayProviderProps>> = ({ scrollElement, children }) => (
    <ModalOverlayContext.Provider value={{ scrollElement }}>{children}</ModalOverlayContext.Provider>
);

/**
 * Determines whether modal content should render in overlay mode based on:
 * 1) whether a provider is present, and
 * 2) current viewport breakpoint.
 *
 * Behavior:
 * - If no provider is available, overlay is disabled and `scrollElement` is `null`.
 * - If provider exists, overlay is enabled for non-mobile breakpoints.
 *
 * @returns An object containing:
 * - `useOverlay`: `true` when overlay mode should be used.
 * - `scrollElement`: context-provided scroll container (or `null`).
 */
const useIsModalOverlay = () => {
    const context = useContext(ModalOverlayContext);
    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");

    if (!context) {
        return { useOverlay: false, scrollElement: null };
    }

    return {
        useOverlay: !isMobile,
        scrollElement: context.scrollElement,
    };
};

export default useIsModalOverlay;
